import {
  RestApiClient,
  type OAuth2Tokens,
  type TokenManager
} from '@docyrus/api-client';

import {
  type AuthMode,
  type AuthStatus,
  type DocyrusAuthConfig,
  type DocyrusCalendarEventPayload,
  type DocyrusEmailPayload,
  type DocyrusSharePayload,
  type DocyrusUser,
  type GuidyRoute,
  type HostNavigationHandler,
  type HostNavigationRequestOptions,
  type HostNotificationHandler,
  type RouteChangePayload
} from '../types';

import { detectAuthMode } from './auth-detector';
import { StandaloneOAuth2Auth } from './oauth2-auth';
import { IframeAuth } from './iframe-auth';
import { GuidyBridge } from './guidy-bridge';
import { MsGraphClient } from './ms-graph-client';
import { SsoAuth } from './sso-auth';
import { ReactNativeOAuth2Auth } from '../react-native/rn-oauth2-auth';
import { DEFAULT_API_URL, TOKEN_EXPIRY_BUFFER_SECONDS } from '../constants';
import { syncTokenToCookie, clearTokenCookie } from './cookie-sync';
import { parseJwtExp } from './jwt';

export type AuthStateListener = (state: {
  status: AuthStatus;
  tokens: OAuth2Tokens | null;
  error: Error | null;
  user: DocyrusUser | null;
}) => void;

/**
 * Unified authentication manager.
 * Orchestrates standalone OAuth2 and iframe postMessage modes,
 * exposes a pre-configured RestApiClient, and manages token lifecycle.
 */
export class AuthManager {
  private mode: AuthMode;
  private status: AuthStatus = 'loading';
  private tokens: OAuth2Tokens | null = null;
  private error: Error | null = null;
  private client: RestApiClient | null = null;
  private user: DocyrusUser | null = null;
  private standaloneAuth: StandaloneOAuth2Auth | null = null;
  private iframeAuth: IframeAuth | null = null;
  private guidyBridge: GuidyBridge | null = null;
  private msGraphClient: MsGraphClient | null = null;
  private reactNativeAuth: ReactNativeOAuth2Auth | null = null;
  private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  /** Shared in-flight refresh, so concurrent requests don't trigger parallel refreshes. */
  private refreshInFlight: Promise<string> | null = null;
  private listeners: Set<AuthStateListener> = new Set();
  private navigationHandlers: Set<HostNavigationHandler> = new Set();
  private notificationHandlers: Set<HostNotificationHandler> = new Set();
  private config: DocyrusAuthConfig;
  constructor(config: DocyrusAuthConfig = {}) {
    this.config = config;
    this.mode = config.forceMode
      ?? detectAuthMode(config.allowedHostOrigins);
  }
  /** Whether the provider may refresh tokens on its own. Default: true. */
  private get autoRefreshEnabled(): boolean {
    return this.config.autoRefresh !== false;
  }
  getMode(): AuthMode {
    return this.mode;
  }
  getStatus(): AuthStatus {
    return this.status;
  }
  getTokens(): OAuth2Tokens | null {
    return this.tokens;
  }
  getClient(): RestApiClient | null {
    return this.client;
  }
  getError(): Error | null {
    return this.error;
  }
  getUser(): DocyrusUser | null {
    return this.user;
  }
  /** Re-fetch the current user from the API. No-op if not authenticated. */
  async refreshUser(): Promise<void> {
    await this.fetchUser();
  }
  subscribe(listener: AuthStateListener): () => void {
    this.listeners.add(listener);

    return () => this.listeners.delete(listener);
  }
  private notify(): void {
    for (const listener of this.listeners) {
      listener({
        status: this.status,
        tokens: this.tokens,
        error: this.error,
        user: this.user
      });
    }
  }
  private getInitialTokens(): OAuth2Tokens | null {
    return this.config.initialTokens ?? null;
  }
  /** Initialize the auth manager. Must be called once on mount. */
  async initialize(): Promise<void> {
    try {
      if (this.mode === 'iframe' && this.getInitialTokens()) {
        throw new Error(
          'initialTokens is not supported in iframe mode. '
          + 'Use the host postMessage signin flow or force standalone mode.'
        );
      }

      if (this.mode === 'react-native') {
        await this.initializeReactNativeMode();
      } else if (this.mode === 'iframe') {
        this.initializeIframeMode();
      } else {
        await this.initializeStandaloneMode();
      }
    } catch (err) {
      this.error = err instanceof Error ? err : new Error(String(err));
      this.status = 'unauthenticated';
      this.notify();
    }
  }
  /**
   * Standalone mode initialization.
   * 1. Check if we're returning from an OAuth callback.
   * 2. If not, check for existing tokens in localStorage.
   * 3. If tokens are expired, try to refresh.
   */
  private async initializeStandaloneMode(): Promise<void> {
    this.standaloneAuth = new StandaloneOAuth2Auth(this.config);

    // Case 0: SSO token in URL (agent headless browser flow)
    const ssoAuth = new SsoAuth(this.config);

    if (ssoAuth.hasSsoToken()) {
      const ssoToken = ssoAuth.getSsoToken();

      if (ssoToken) {
        try {
          const tokens = await ssoAuth.exchange(ssoToken);

          await this.standaloneAuth.setTokens(tokens);
          this.setAuthenticated(tokens);
          ssoAuth.cleanUrl();

          return;
        } catch (err) {
          // SSO exchange failed — fall through to normal auth flow
          console.warn('[docyrus-signin] SSO token exchange failed:', err);
        }
      }
    }

    // Case 1: We're at the callback URL after OAuth redirect
    if (this.standaloneAuth.isCallbackUrl()) {
      const tokens = await this.standaloneAuth.handleCallback();

      this.setAuthenticated(tokens);

      // Navigate away from the callback URL
      const returnUrl = this.standaloneAuth.getReturnUrl();

      if (returnUrl) {
        window.location.replace(returnUrl);
      } else {
        window.history.replaceState({}, '', '/');
      }

      return;
    }

    const initialTokens = this.getInitialTokens();

    if (initialTokens) {
      await this.standaloneAuth.setTokens(initialTokens);
      this.setAuthenticated(initialTokens);

      return;
    }

    // Case 2: Check for existing tokens
    const stored = await this.standaloneAuth.getStoredTokens();

    if (stored) {
      const isExpired = await this.standaloneAuth.isTokenExpired();

      if (!isExpired) {
        this.setAuthenticated(stored);

        return;
      }

      // Token expired, try refresh
      if (stored.refreshToken) {
        try {
          const newTokens = await this.standaloneAuth.refreshToken();

          this.setAuthenticated(newTokens);

          return;
        } catch {
          // Refresh failed, require re-login
        }
      }
    }

    // Case 3: No tokens or refresh failed
    this.status = 'unauthenticated';
    this.notify();
  }
  /**
   * Iframe mode initialization.
   * Listen for postMessage tokens from the host.
   * Status remains 'loading' until the host sends the first signin message.
   */
  private initializeIframeMode(): void {
    this.iframeAuth = new IframeAuth(this.config.allowedHostOrigins);
    this.msGraphClient = new MsGraphClient(this.iframeAuth);

    this.iframeAuth.start((tokens: OAuth2Tokens) => {
      this.setAuthenticated(tokens);
    });

    // Bridge host messages to consumer-registered handlers
    this.iframeAuth.onNavigation((message) => {
      for (const handler of this.navigationHandlers) {
        try {
          handler(message);
        } catch (err) {
          console.warn('[docyrus-signin] host navigation handler threw:', err);
        }
      }
    });

    this.iframeAuth.onNotification((notification) => {
      for (const handler of this.notificationHandlers) {
        try {
          handler(notification);
        } catch (err) {
          console.warn('[docyrus-signin] host notification handler threw:', err);
        }
      }
    });

    if (this.config.syncRouteToHost) {
      this.iframeAuth.enableRouteSync();
    }

    if (this.config.enableGuidyBridge) {
      this.startGuidyBridge();
    }
  }
  /**
   * Start the Guidy bridge runtime: report the app's clickable inventory to
   * the host and execute host scan/point/click commands. Iframe mode only.
   */
  private startGuidyBridge(): void {
    if (!this.iframeAuth || this.guidyBridge) return;

    const bridge = new GuidyBridge(
      message => this.iframeAuth?.sendToHost(message),
      this.config.guidyRoutes ?? []
    );

    this.iframeAuth.onGuidyCommand(message => bridge.handleCommand(message));
    bridge.start();
    this.guidyBridge = bridge;
  }
  /**
   * Iframe mode: replace the routes the Guidy bridge advertises to the host.
   * No-op unless the bridge is running (`enableGuidyBridge`).
   */
  setGuidyRoutes(routes: GuidyRoute[]): void {
    this.config.guidyRoutes = routes;
    this.guidyBridge?.setRoutes(routes);
  }
  /**
   * Iframe/WebView mode: start auto-syncing the embedded app's route
   * to the host. Patches history methods and listens for popstate /
   * hashchange. Idempotent. No-op outside iframe mode.
   */
  enableHostRouteSync(): void {
    this.iframeAuth?.enableRouteSync();
  }
  /**
   * Iframe/WebView mode: post a single `route-change` message to the
   * host. With no argument, reads `window.location`. Pass a payload
   * from a router subscription to report a known route. No-op outside
   * iframe mode.
   */
  notifyHostRouteChange(payload?: RouteChangePayload): void {
    this.iframeAuth?.notifyRouteChange(payload);
  }
  /**
   * Iframe/WebView mode: ask the host shell to navigate to a path/URL.
   * Posts a `navigation-request` message; the host decides how to honour it.
   * No-op outside iframe mode.
   */
  requestHostNavigation(url: string, options?: HostNavigationRequestOptions): void {
    this.iframeAuth?.requestHostNavigation(url, options);
  }
  /**
   * Iframe/WebView mode: hand a payload to the host AI assistant (Docy).
   * Resolves when the host acknowledges delivery. Rejects outside iframe mode.
   */
  sendToDocy(payload: DocyrusSharePayload): Promise<void> {
    if (!this.iframeAuth) {
      return Promise.reject(new Error('sendToDocy() is only available in iframe/WebView mode'));
    }

    return this.iframeAuth.sendToDocy(payload);
  }
  /**
   * Iframe/WebView mode: hand a payload to the host team-chat app.
   * Resolves when the host acknowledges delivery. Rejects outside iframe mode.
   */
  sendToChat(payload: DocyrusSharePayload): Promise<void> {
    if (!this.iframeAuth) {
      return Promise.reject(new Error('sendToChat() is only available in iframe/WebView mode'));
    }

    return this.iframeAuth.sendToChat(payload);
  }
  /**
   * Iframe/WebView mode: hand a draft email to the host. Resolves when the
   * host acknowledges. Rejects outside iframe mode.
   */
  sendToEmail(payload: DocyrusEmailPayload): Promise<void> {
    if (!this.iframeAuth) {
      return Promise.reject(new Error('sendToEmail() is only available in iframe/WebView mode'));
    }

    return this.iframeAuth.sendToEmail(payload);
  }
  /**
   * Iframe/WebView mode: hand a calendar event to the host. Resolves when the
   * host acknowledges. Rejects outside iframe mode.
   */
  sendToCalendar(payload: DocyrusCalendarEventPayload): Promise<void> {
    if (!this.iframeAuth) {
      return Promise.reject(new Error('sendToCalendar() is only available in iframe/WebView mode'));
    }

    return this.iframeAuth.sendToCalendar(payload);
  }
  /**
   * Iframe/WebView mode: get the client for the host's Microsoft Graph (MSAL)
   * session. Returns `null` outside iframe mode.
   */
  getMsGraphClient(): MsGraphClient | null {
    return this.msGraphClient;
  }
  /**
   * Subscribe to host `navigation` messages. Returns an unsubscribe function.
   * Handlers registered before iframe mode is initialized are retained and
   * will fire once messages arrive. No-op in non-iframe modes.
   */
  onHostNavigation(handler: HostNavigationHandler): () => void {
    this.navigationHandlers.add(handler);

    return () => {
      this.navigationHandlers.delete(handler);
    };
  }
  /**
   * Subscribe to host `notification` messages. Returns an unsubscribe function.
   * Handlers registered before iframe mode is initialized are retained and
   * will fire once messages arrive. No-op in non-iframe modes.
   */
  onHostNotification(handler: HostNotificationHandler): () => void {
    this.notificationHandlers.add(handler);

    return () => {
      this.notificationHandlers.delete(handler);
    };
  }
  /**
   * React Native mode initialization.
   * No callback URL check needed — the in-app browser returns the URL directly.
   * Just check for existing tokens and refresh if expired.
   */
  private async initializeReactNativeMode(): Promise<void> {
    this.reactNativeAuth = new ReactNativeOAuth2Auth(this.config);

    const initialTokens = this.getInitialTokens();

    if (initialTokens) {
      await this.reactNativeAuth.setTokens(initialTokens);
      this.setAuthenticated(initialTokens);

      return;
    }

    // Check for existing tokens in storage
    const stored = await this.reactNativeAuth.getStoredTokens();

    if (stored) {
      const isExpired = await this.reactNativeAuth.isTokenExpired();

      if (!isExpired) {
        this.setAuthenticated(stored);

        return;
      }

      // Token expired, try refresh
      if (stored.refreshToken) {
        try {
          const newTokens = await this.reactNativeAuth.refreshToken();

          this.setAuthenticated(newTokens);

          return;
        } catch {
          // Refresh failed, require re-login
        }
      }
    }

    // No tokens or refresh failed
    this.status = 'unauthenticated';
    this.notify();
  }
  /** Initiate sign-in. Works in standalone and react-native modes. */
  async signIn(): Promise<void> {
    if (this.mode === 'react-native' && this.reactNativeAuth) {
      try {
        const tokens = await this.reactNativeAuth.initiateLogin();

        this.setAuthenticated(tokens);
      } catch (err) {
        this.error = err instanceof Error ? err : new Error(String(err));
        this.notify();
      }

      return;
    }

    if (this.mode !== 'standalone' || !this.standaloneAuth) return;

    await this.standaloneAuth.initiateLogin();
  }
  /**
   * Bootstrap an authenticated session from tokens obtained outside the
   * browser redirect flow, such as a prior device authorization flow.
   */
  async signInWithTokens(tokens: OAuth2Tokens): Promise<void> {
    if (this.mode === 'iframe') {
      throw new Error(
        'signInWithTokens() is not supported in iframe mode. '
        + 'Use the host postMessage signin flow or force standalone mode.'
      );
    }

    if (this.mode === 'react-native') {
      if (!this.reactNativeAuth) {
        this.reactNativeAuth = new ReactNativeOAuth2Auth(this.config);
      }

      await this.reactNativeAuth.setTokens(tokens);
      this.setAuthenticated(tokens);

      return;
    }

    if (!this.standaloneAuth) {
      this.standaloneAuth = new StandaloneOAuth2Auth(this.config);
    }

    await this.standaloneAuth.setTokens(tokens);
    this.setAuthenticated(tokens);
  }
  /**
   * Get the OAuth2 authorization URL without navigating.
   * Use this in Electron apps to open the URL in an external browser.
   * Returns null if not in standalone mode.
   */
  async getAuthorizationUrl(): Promise<string | null> {
    if (this.mode !== 'standalone' || !this.standaloneAuth) return null;

    return this.standaloneAuth.getAuthorizationUrl();
  }
  /**
   * Sign out.
   * Standalone: revoke token, clear localStorage, reset state.
   * Iframe: clear local state (host manages the actual session).
   */
  async signOut(): Promise<void> {
    this.clearTokenRefreshTimer();

    if (this.mode === 'standalone' && this.standaloneAuth) {
      await this.standaloneAuth.logout();
    }

    if (this.mode === 'react-native' && this.reactNativeAuth) {
      await this.reactNativeAuth.logout();
    }

    if (this.mode === 'iframe' && this.iframeAuth) {
      if (this.guidyBridge) {
        this.guidyBridge.stop();
        this.guidyBridge = null;
      }

      this.iframeAuth.stop();
      this.msGraphClient = null;
    }

    if (this.config.ssr) {
      clearTokenCookie(this.config.ssrCookieKey);
    }

    this.tokens = null;
    this.client = null;
    this.user = null;
    this.status = 'unauthenticated';
    this.error = null;
    this.notify();
  }
  /**
   * Called when valid tokens are received (either mode).
   * Creates/updates the RestApiClient and schedules token refresh.
   * Fires a user fetch in the background — the first notify() fires with user: null,
   * and a second notify() fires once the user is fetched.
   */
  private setAuthenticated(tokens: OAuth2Tokens): void {
    this.tokens = tokens;
    this.error = null;
    this.status = 'authenticated';

    if (this.config.ssr) {
      syncTokenToCookie(tokens.accessToken, this.config.ssrCookieKey, tokens.expiresAt);
    }

    this.createClient();
    this.scheduleTokenRefresh(tokens);
    this.notify();

    // Fire-and-forget user fetch
    this.fetchUser();
  }
  /** Fetch the current user from /v1/users/me. Silently sets user to null on failure. */
  private async fetchUser(): Promise<void> {
    if (!this.client) return;

    try {
      const response = await this.client.get<{ data: DocyrusUser }>('/v1/users/me');

      /*
       * Handle both wrapped ({ success, data }) and unwrapped (direct user) responses.
       * Consumer apps may add response interceptors that strip the API envelope.
       */
      const maybeUser = response as unknown as DocyrusUser;

      this.user = (maybeUser && typeof maybeUser === 'object' && 'id' in maybeUser && 'email' in maybeUser) ? maybeUser : response.data;
    } catch {
      this.user = null;
    }

    this.notify();
  }
  /**
   * Create a RestApiClient with a custom TokenManager that proactively
   * refreshes the token in getToken(). This is necessary because
   * BaseApiClient.getAccessToken() only calls tokenManager.getToken()
   * and does NOT auto-call refreshToken() when the token is expired.
   */
  private createClient(): void {
    const apiUrl = this.config.apiUrl ?? DEFAULT_API_URL;

    const tokenManager: TokenManager = {
      getToken: () => this.getValidToken(),
      setToken: (token: string) => {
        if (this.tokens) {
          this.tokens = { ...this.tokens, accessToken: token };
        }
      },
      clearToken: () => {
        this.tokens = null;
      }
    };

    /*
     * Only wire the reactive (401-triggered) refresh when auto-refresh is
     * enabled. With it omitted, RestApiClient surfaces the 401 instead of
     * attempting a refresh the consumer has opted out of.
     */
    if (this.autoRefreshEnabled) {
      tokenManager.refreshToken = () => this.handleTokenRefresh();
    }

    this.client = new RestApiClient({
      baseURL: apiUrl,
      tokenManager
    });
  }
  /**
   * Get a valid access token, proactively refreshing if expired.
   * Called by the RestApiClient on every request via tokenManager.getToken().
   */
  private async getValidToken(): Promise<string | null> {
    if (!this.tokens) return null;

    if (this.autoRefreshEnabled) {
      const expiresAt = this.resolveExpiry(this.tokens);
      const nowSec = Math.floor(Date.now() / 1000);

      /*
       * Refresh when the token is within the expiry buffer, or when its
       * expiry can't be resolved at all — an unparseable token must never be
       * shipped blind, since that's how an expired one reaches the backend.
       */
      const needsRefresh = expiresAt === null || nowSec >= expiresAt - TOKEN_EXPIRY_BUFFER_SECONDS;

      if (needsRefresh) {
        try {
          return await this.handleTokenRefresh();
        } catch {
          /*
           * Known-expired → no usable token. Unknown expiry → fall back to
           * what we have rather than blocking a possibly-valid token.
           */
          return expiresAt === null ? this.tokens.accessToken : null;
        }
      }
    }

    return this.tokens.accessToken;
  }
  /**
   * Handle token refresh depending on mode, sharing a single in-flight
   * request across concurrent callers. Multiple requests crossing the expiry
   * buffer at once (e.g. a dashboard firing several queries) would otherwise
   * each trigger their own refresh; with refresh-token rotation only the first
   * succeeds and the rest can leave us holding an expired token — exactly the
   * backend "JWTExpired" the proactive path is meant to prevent.
   */
  private handleTokenRefresh(): Promise<string> {
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = this.performTokenRefresh().finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }
  /**
   * Refresh depending on mode.
   * Standalone: use OAuth2Client.getValidAccessToken() which auto-refreshes.
   * Iframe: send postMessage to host requesting new tokens.
   */
  private async performTokenRefresh(): Promise<string> {
    if (this.mode === 'standalone' && this.standaloneAuth) {
      const newTokens = await this.standaloneAuth.refreshToken();

      this.tokens = newTokens;

      if (this.config.ssr) {
        syncTokenToCookie(newTokens.accessToken, this.config.ssrCookieKey, newTokens.expiresAt);
      }

      this.scheduleTokenRefresh(newTokens);
      this.notify();

      return newTokens.accessToken;
    }

    if (this.mode === 'react-native' && this.reactNativeAuth) {
      const newTokens = await this.reactNativeAuth.refreshToken();

      this.tokens = newTokens;
      this.scheduleTokenRefresh(newTokens);
      this.notify();

      return newTokens.accessToken;
    }

    if (this.mode === 'iframe' && this.iframeAuth) {
      const newTokens = await this.iframeAuth.requestTokenRefresh();

      return newTokens.accessToken;
    }

    throw new Error('Cannot refresh token: auth not initialized');
  }
  /** Schedule proactive token refresh before expiry. */
  private scheduleTokenRefresh(tokens: OAuth2Tokens): void {
    this.clearTokenRefreshTimer();

    if (!this.autoRefreshEnabled) return;

    const expiresAt = this.resolveExpiry(tokens);

    if (!expiresAt) return;

    const expiresAtMs = expiresAt * 1000;
    const bufferMs = TOKEN_EXPIRY_BUFFER_SECONDS * 1000;
    const refreshInMs = expiresAtMs - bufferMs - Date.now();

    if (refreshInMs <= 0) {
      // Already needs refresh
      this.handleTokenRefresh().catch((err) => {
        this.error = err instanceof Error ? err : new Error(String(err));
        this.status = 'unauthenticated';
        this.notify();
      });

      return;
    }

    this.tokenRefreshTimer = setTimeout(() => {
      this.handleTokenRefresh().catch((err) => {
        this.error = err instanceof Error ? err : new Error(String(err));
        this.status = 'unauthenticated';
        this.notify();
      });
    }, refreshInMs);
  }
  private clearTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }
  /**
   * Resolve token expiry from stored expiresAt, falling back to the JWT exp
   * claim when expiresAt is absent (e.g. server omitted expires_in).
   */
  private resolveExpiry(tokens: OAuth2Tokens): number | null {
    if (tokens.expiresAt) return tokens.expiresAt;

    return parseJwtExp(tokens.accessToken);
  }
  /** Cleanup: remove listeners, timers, stop iframe auth. */
  destroy(): void {
    this.clearTokenRefreshTimer();

    if (this.guidyBridge) {
      this.guidyBridge.stop();
      this.guidyBridge = null;
    }

    if (this.iframeAuth) this.iframeAuth.stop();
    this.msGraphClient = null;

    this.listeners.clear();
    this.navigationHandlers.clear();
    this.notificationHandlers.clear();
  }
}
